import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtNum, fmtInt } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import BudgetQuickPicker from "../components/BudgetQuickPicker";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, Search, ChevronDown, ChevronRight, Filter,
  Pause, Play, X, Plus, Pencil, Copy,
} from "lucide-react";
import { toast } from "sonner";

const RANGE_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
  { value: "custom", label: "Custom" },
];

const COLUMNS = [
  { key: "ad_name", label: "Ad name", align: "left" },
  { key: "spend", label: "Spend", align: "right", money: true },
  { key: "impressions", label: "Impressions", align: "right", int: true },
  { key: "clicks", label: "Clicks", align: "right", int: true },
  { key: "ctr_clicks", label: "CTR", align: "right", pct: true },
  { key: "registrations", label: "Plat. Reg.", align: "right", int: true },
  { key: "cost_per_registration", label: "Plat. CPA", align: "right", money: true, colorize: true },
  { key: "tm_registrations", label: "TM Reg.", align: "right", num1: true },
  { key: "tm_cpa", label: "TM CPA", align: "right", money: true, colorize: true },
  { key: "variance_pct", label: "Var. %", align: "right", variance: true },
];

export default function MetaAds() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState([]); // array of account_id (act_xxx)
  const [days, setDays] = useState("7");
  const [startDate, setStart] = useState("");
  const [endDate, setEnd] = useState("");
  const [minSpend, setMinSpend] = useState("0");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("spend");
  const [sortDir, setSortDir] = useState("desc");
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [quickBusy, setQuickBusy] = useState(null);
  const [tab, setTab] = useState("all"); // all | running | paused | new | drafts
  const [selectedAds, setSelectedAds] = useState(new Set()); // ad_name keys
  const [bulkBusy, setBulkBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);

  const loadDrafts = async () => {
    setDraftsLoading(true);
    try {
      const r = await api.get("/meta/ad-drafts");
      setDrafts(r.data.drafts || []);
    } catch (e) {
      toast.error("Drafts load failed", { description: e.response?.data?.detail || e.message });
    } finally { setDraftsLoading(false); }
  };
  useEffect(() => { loadDrafts(); }, []);

  const deleteDraft = async (id) => {
    if (!window.confirm("Delete this draft?")) return;
    await api.delete(`/meta/ad-drafts/${id}`);
    toast.success("Draft deleted");
    await loadDrafts();
  };

  const [launchingDraftId, setLaunchingDraftId] = useState(null);
  const [lastDraftResult, setLastDraftResult] = useState(null);
  const launchDraft = async (id, initial_status) => {
    if (!window.confirm(`Launch this draft ${initial_status === "ACTIVE" ? "ACTIVE" : "PAUSED"}? Meta may take 30-60s to process each ad set.`)) return;
    setLaunchingDraftId(`${id}-${initial_status}`);
    setLastDraftResult(null);
    try {
      const r = await api.post(`/meta/ad-drafts/${id}/launch`, null, { params: { initial_status } });
      const { success, failure, results } = r.data;
      setLastDraftResult({ draft_id: id, ...r.data });
      if (failure > 0) {
        const devModeHit = (results || []).some((rr) => (rr.error || "").includes("1885183"));
        const firstErr = (results || []).find((rr) => !rr.ok)?.error || "see details below";
        const desc = devModeHit
          ? "Meta App is still in dev mode. Re-check the access token."
          : `${firstErr}`.slice(0, 250);
        toast.error(`Launched ${success}/${success + failure} (${failure} failed)`, { description: desc, duration: 14000 });
      } else {
        toast.success(`Launched ${success} ad placement(s)`);
      }
      await loadDrafts();
      await load();
    } catch (e) {
      toast.error("Launch failed", { description: e.response?.data?.detail || e.message });
    } finally { setLaunchingDraftId(null); }
  };

  const quickCampaign = async (b, direction, pct = 20) => {
    setQuickBusy(`camp-${b.campaign_id}-${direction}-${pct}`);
    try {
      const r = await api.post(`/campaigns/${b.campaign_id}/quick-budget`, {
        direction, pct, name: b.campaign_name, network: "meta",
      });
      toast.success(`${direction === "increase" ? "+" : "−"}${pct}% on campaign ${b.campaign_name.slice(0, 30)}`,
        { description: `£${r.data.current_budget.toFixed(2)} → £${r.data.new_budget.toFixed(2)}` });
    } catch (e) {
      toast.error("Campaign budget update failed", { description: e.response?.data?.detail || e.message });
    } finally { setQuickBusy(null); }
  };

  const quickAdset = async (b, direction, pct = 20) => {
    setQuickBusy(`ad-${b.adset_id}-${direction}-${pct}`);
    try {
      const r = await api.post(`/adsets/${b.adset_id}/quick-budget`, { direction, pct });
      toast.success(`${direction === "increase" ? "+" : "−"}${pct}% on ad set ${b.adset_name.slice(0, 30)}`,
        { description: `£${r.data.current_budget.toFixed(2)} → £${r.data.new_budget.toFixed(2)}` });
    } catch (e) {
      toast.error("Ad set budget update failed", { description: e.response?.data?.detail || e.message });
    } finally { setQuickBusy(null); }
  };

  // Load Meta ad accounts
  useEffect(() => {
    api.get("/accounts").then((r) => {
      const meta = (r.data.accounts || []).filter((a) => a.network === "meta" && a.active);
      setAccounts(meta);
      setSelected(meta.map((a) => a.account_id));
    }).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { min_spend: minSpend || 0 };
      if (days === "custom") {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      } else {
        params.days = days;
      }
      if (selected.length > 0) params.account_ids = selected.join(",");
      const r = await api.get("/meta/ads", { params });
      setAds(r.data.ads || []);
    } catch (e) {
      toast.error("Failed to load Meta ads", { description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selected.length > 0) load();
    // eslint-disable-next-line
  }, [days, startDate, endDate, minSpend, selected.join(",")]);

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = ads;
    if (tab === "running") list = list.filter((a) => a.overall_status === "running");
    else if (tab === "paused") list = list.filter((a) => a.overall_status === "paused" || a.overall_status === "archived");
    else if (tab === "new") list = list.filter((a) => a.is_draft);
    if (q) {
      list = list.filter((a) => (a.ad_name || "").toLowerCase().includes(q));
    }
    const arr = [...list];
    arr.sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (typeof av === "string" || typeof bv === "string") {
        av = (av || "").toString().toLowerCase();
        bv = (bv || "").toString().toLowerCase();
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      }
      return sortDir === "asc" ? (Number(av) - Number(bv)) : (Number(bv) - Number(av));
    });
    return arr;
  }, [ads, search, sortKey, sortDir, tab]);

  // Pre-compute counts for tab labels
  const counts = useMemo(() => ({
    all: ads.length,
    running: ads.filter((a) => a.overall_status === "running").length,
    paused: ads.filter((a) => a.overall_status === "paused" || a.overall_status === "archived").length,
    new: ads.filter((a) => a.is_draft).length,
  }), [ads]);

  const sortIcon = (key) => {
    if (key !== sortKey) return <ArrowUpDown className="w-3 h-3 inline opacity-30" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3 inline" /> : <ArrowDown className="w-3 h-3 inline" />;
  };

  const handleSort = (key) => {
    if (key === sortKey) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(key === "ad_name" ? "asc" : "desc"); }
  };

  const toggleAccount = (id) => {
    setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };

  const selectAll = () => setSelected(accounts.map((a) => a.account_id));
  const selectNone = () => setSelected([]);

  // ----- Bulk selection (ads) -----
  const toggleAd = (adName) => {
    setSelectedAds((s) => {
      const next = new Set(s);
      if (next.has(adName)) next.delete(adName); else next.add(adName);
      return next;
    });
  };
  const allVisibleSelected = (filteredSortedList) =>
    filteredSortedList.length > 0 && filteredSortedList.every((a) => selectedAds.has(a.ad_name));
  const toggleSelectAllVisible = (filteredSortedList) => {
    setSelectedAds((s) => {
      const next = new Set(s);
      if (allVisibleSelected(filteredSortedList)) {
        filteredSortedList.forEach((a) => next.delete(a.ad_name));
      } else {
        filteredSortedList.forEach((a) => next.add(a.ad_name));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedAds(new Set());

  const selectedAdIds = useMemo(() => {
    const out = [];
    for (const ad of ads) {
      if (selectedAds.has(ad.ad_name)) out.push(...(ad.ad_ids || []));
    }
    return out;
  }, [ads, selectedAds]);

  const bulkSetStatus = async (status) => {
    if (selectedAdIds.length === 0) return;
    const verb = status === "PAUSED" ? "Pause" : "Resume";
    if (!window.confirm(`${verb} ${selectedAdIds.length} ad placement(s) across ${selectedAds.size} ad(s)?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/meta/ads/bulk-status", { ad_ids: selectedAdIds, status });
      const { success, failure } = r.data || {};
      if (failure > 0) {
        toast.error(`${verb} partial: ${success} ok / ${failure} failed`,
          { description: "Check the activity log for per-ad errors." });
      } else {
        toast.success(`${verb}d ${success} ad placement(s)`);
      }
      clearSelection();
      await load();
    } catch (e) {
      toast.error(`${verb} failed`, { description: e.response?.data?.detail || e.message });
    } finally {
      setBulkBusy(false);
    }
  };

  // Avg cost-per-registration across positive rows, used for green/red CPA coloring
  const avgCpr = useMemo(() => {
    const pos = ads.filter((a) => (a.cost_per_registration || 0) > 0);
    if (!pos.length) return 0;
    return pos.reduce((s, a) => s + a.cost_per_registration, 0) / pos.length;
  }, [ads]);

  const cpaColorCls = (v) => {
    if (!v || v <= 0) return "";
    if (avgCpr && v < avgCpr) return "bg-emerald-50 text-emerald-700";
    if (avgCpr && v > avgCpr * 1.5) return "bg-red-50 text-red-700";
    return "";
  };

  const fmtCell = (col, value) => {
    if (value === null || value === undefined) return "—";
    if (col.money) return fmtMoney(value);
    if (col.int) return fmtInt(value);
    if (col.num1) return fmtNum(value, 1);
    if (col.pct) return `${Number(value).toFixed(2)}%`;
    if (col.variance) return `${value > 0 ? "+" : ""}${Number(value).toFixed(0)}%`;
    return value;
  };

  const varianceClass = (v) => {
    if (v === null || v === undefined) return "text-zinc-400";
    if (Math.abs(v) >= 25) return "bg-red-50 text-red-700 px-1.5";
    if (Math.abs(v) >= 10) return "bg-amber-50 text-amber-700 px-1.5";
    return "text-zinc-600";
  };

  return (
    <div data-testid="meta-ads-page" className="fade-in">
      <PageHeader
        eyebrow="Creative Performance"
        title="Meta Ads"
        description="Ad-level Meta data aggregated by ad name. Pick ad accounts via checkboxes, expand any row to see per-ad-set breakdown."
        actions={
          <div className="flex items-center gap-2">
            <Button data-testid="meta-create-ad-btn" onClick={() => navigate("/meta-ads/create")}
              className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white font-mono uppercase tracking-wider text-xs">
              <Plus className="w-4 h-4 mr-2" /> Create ad
            </Button>
            <Button data-testid="meta-refresh-btn" onClick={load} disabled={loading}
              className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-wider text-xs">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 space-y-6">
        {/* Filters */}
        <div className="bg-white border border-zinc-200 p-5">
          <div className="data-label mb-3">Filters</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <div className="lg:col-span-2">
              <label className="data-label block mb-1">Ad accounts ({selected.length}/{accounts.length})</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid="meta-accounts-trigger" variant="outline" className="rounded-none border-zinc-300 font-mono text-sm w-full justify-between">
                    <span className="truncate text-left">
                      {selected.length === accounts.length
                        ? "All Meta accounts"
                        : selected.length === 0
                        ? "Select accounts…"
                        : `${selected.length} account(s)`}
                    </span>
                    <ChevronDown className="w-4 h-4 ml-2 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-96 rounded-none p-0 border-zinc-300">
                  <div className="px-3 py-2 border-b border-zinc-200 flex items-center justify-between">
                    <div className="data-label">{accounts.length} Meta accounts</div>
                    <div className="flex gap-2 text-[10px] font-mono uppercase">
                      <button onClick={selectAll} data-testid="acct-select-all" className="hover:underline">all</button>
                      <span className="text-zinc-300">·</span>
                      <button onClick={selectNone} data-testid="acct-select-none" className="hover:underline">none</button>
                    </div>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {accounts.map((a) => (
                      <label key={a.account_id} className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 cursor-pointer"
                             data-testid={`acct-row-${a.account_id}`}>
                        <Checkbox checked={selected.includes(a.account_id)} onCheckedChange={() => toggleAccount(a.account_id)} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm truncate">{a.name}</div>
                          <div className="text-xs text-zinc-500 font-mono">{a.account_id} · {a.currency || "—"}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="data-label block mb-1">Date range</label>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger data-testid="meta-daterange" className="rounded-none border-zinc-300 font-mono text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RANGE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {days === "custom" && (
              <>
                <div>
                  <label className="data-label block mb-1">Start</label>
                  <Input data-testid="meta-start-date" type="date" value={startDate} onChange={(e) => setStart(e.target.value)} className="rounded-none border-zinc-300 font-mono text-sm" />
                </div>
                <div>
                  <label className="data-label block mb-1">End</label>
                  <Input data-testid="meta-end-date" type="date" value={endDate} onChange={(e) => setEnd(e.target.value)} className="rounded-none border-zinc-300 font-mono text-sm" />
                </div>
              </>
            )}
            <div>
              <label className="data-label block mb-1">Min spend</label>
              <Input data-testid="meta-min-spend" type="number" value={minSpend} onChange={(e) => setMinSpend(e.target.value)} className="rounded-none border-zinc-300 font-mono text-sm" />
            </div>
          </div>
        </div>

        {/* Bulk action bar (visible when ads are selected) */}
        {selectedAds.size > 0 && (
          <div data-testid="bulk-action-bar"
               className="bg-zinc-900 text-white px-5 py-3 flex items-center justify-between flex-wrap gap-3 sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <button
                data-testid="bulk-clear-selection"
                onClick={clearSelection}
                title="Clear selection"
                className="text-zinc-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="font-mono text-xs uppercase tracking-wider">
                {selectedAds.size} ad{selectedAds.size === 1 ? "" : "s"} selected
                <span className="opacity-60 ml-2 normal-case">({selectedAdIds.length} placement{selectedAdIds.length === 1 ? "" : "s"})</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="bulk-pause-btn"
                disabled={bulkBusy}
                onClick={() => bulkSetStatus("PAUSED")}
                className="rounded-none bg-amber-500 hover:bg-amber-600 text-white font-mono uppercase tracking-wider text-xs h-9 px-4"
              >
                <Pause className="w-3.5 h-3.5 mr-1.5" />
                Pause selected
              </Button>
              <Button
                data-testid="bulk-resume-btn"
                disabled={bulkBusy}
                onClick={() => bulkSetStatus("ACTIVE")}
                className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white font-mono uppercase tracking-wider text-xs h-9 px-4"
              >
                <Play className="w-3.5 h-3.5 mr-1.5" />
                Resume selected
              </Button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-zinc-200">
          <div className="px-5 pt-4 pb-0 border-b border-zinc-200">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
              <div>
                <div className="data-label">Ads</div>
                <div className="font-mono text-sm text-zinc-600 mt-1">
                  {loading ? "Loading…" : `${filteredSorted.length} of ${ads.length} ads`}
                </div>
              </div>
              <div className="relative w-full md:w-80">
                <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="meta-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by ad name…" className="rounded-none border-zinc-300 pl-9 font-mono text-sm" />
              </div>
            </div>
            <div className="inline-flex" data-testid="meta-ads-tabs">
              {[
                { v: "all", label: "All" },
                { v: "running", label: "Running" },
                { v: "paused", label: "Paused / Archived" },
                { v: "new", label: "New (recent)" },
                { v: "drafts", label: "Drafts" },
              ].map((t, i) => (
                <button
                  key={t.v}
                  data-testid={`meta-ads-tab-${t.v}`}
                  onClick={() => setTab(t.v)}
                  className={`px-4 h-9 text-xs font-mono uppercase tracking-wider border border-b-0 ${i > 0 ? "-ml-px" : ""} ${
                    tab === t.v
                      ? "bg-zinc-900 text-white border-zinc-900"
                      : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                  }`}>
                  {t.label}
                  <span className="ml-2 opacity-70">{t.v === "drafts" ? drafts.length : (counts[t.v] ?? 0)}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            {tab === "drafts" ? (
              <>
              {lastDraftResult && (
                <div className={`m-3 p-4 border ${lastDraftResult.failure > 0 ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50"}`}
                     data-testid="draft-launch-result">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-sm">
                      Last launch · {lastDraftResult.success} ok · {lastDraftResult.failure} failed
                    </div>
                    <button onClick={() => setLastDraftResult(null)} className="text-zinc-500 hover:text-zinc-900 p-1">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <ul className="text-xs font-mono mt-2 space-y-1 max-h-60 overflow-auto">
                    {(lastDraftResult.results || []).map((r, i) => (
                    <li key={i} className={r.ok ? "text-emerald-800" : "text-red-700"}>
                      {r.ok ? "✓" : "✗"} {r.adset_name || r.adset_id} {r.ok ? `→ ad ${r.ad_id}` : ""}
                      {!r.ok && r.hint && (
                        <div className="mt-1 ml-4 px-2 py-1 bg-amber-100 border border-amber-300 text-amber-900 text-[11px] font-sans normal-case">
                          {r.hint}
                        </div>
                      )}
                      {!r.ok && !r.hint && r.error && (
                        <div className="mt-1 ml-4 text-[10px] text-red-700 break-all">{r.error}</div>
                      )}
                    </li>
                    ))}
                  </ul>
                </div>
              )}
              <table data-testid="drafts-table" className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-zinc-50">
                    <th className="text-left px-5 py-3 data-label">Draft name</th>
                    <th className="text-left px-3 py-3 data-label">Type</th>
                    <th className="text-left px-3 py-3 data-label">Account</th>
                    <th className="text-left px-3 py-3 data-label">Ad sets</th>
                    <th className="text-left px-3 py-3 data-label">Last edited</th>
                    <th className="text-right px-3 py-3 data-label">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {draftsLoading && (
                    <tr><td colSpan={6} className="text-center py-10 text-zinc-500 font-mono text-sm">Loading…</td></tr>
                  )}
                  {!draftsLoading && drafts.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-10 text-zinc-500 font-mono text-sm">
                      No drafts yet. Use <span className="text-zinc-700">+ Create ad</span> and click <span className="text-zinc-700">Save draft</span>.
                    </td></tr>
                  )}
                  {drafts.map((d) => (
                    <tr key={d.id} data-testid={`draft-row-${d.id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-5 py-3 max-w-md">
                        <div className="font-medium text-zinc-900 truncate">{d.name}</div>
                        <div className="text-xs text-zinc-500 font-mono">{d.headline || "—"}</div>
                      </td>
                      <td className="px-3 py-3 text-xs font-mono uppercase">{d.creative_type}</td>
                      <td className="px-3 py-3 text-xs font-mono">{d.account_id}</td>
                      <td className="px-3 py-3 text-xs font-mono">{(d.adsets || []).length}</td>
                      <td className="px-3 py-3 text-xs font-mono">{(d.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <button data-testid={`draft-edit-${d.id}`} onClick={() => navigate(`/meta-ads/create?draft=${d.id}`)}
                          className="px-2 py-1 text-[11px] font-mono uppercase tracking-wider border border-zinc-300 hover:bg-zinc-100">Edit</button>
                        <button data-testid={`draft-launch-paused-${d.id}`}
                          onClick={() => launchDraft(d.id, "PAUSED")}
                          disabled={!(d.adsets || []).length || !!launchingDraftId}
                          className="ml-1 px-2 py-1 text-[11px] font-mono uppercase tracking-wider border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-40">
                          {launchingDraftId === `${d.id}-PAUSED` ? "Launching…" : "Launch paused"}
                        </button>
                        <button data-testid={`draft-launch-active-${d.id}`}
                          onClick={() => launchDraft(d.id, "ACTIVE")}
                          disabled={!(d.adsets || []).length || !!launchingDraftId}
                          className="ml-1 px-2 py-1 text-[11px] font-mono uppercase tracking-wider border border-emerald-600 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">
                          {launchingDraftId === `${d.id}-ACTIVE` ? "Launching…" : "Launch active"}
                        </button>
                        <button data-testid={`draft-delete-${d.id}`} onClick={() => deleteDraft(d.id)} disabled={!!launchingDraftId}
                          className="ml-1 px-2 py-1 text-[11px] font-mono uppercase tracking-wider border border-zinc-300 text-red-700 hover:bg-red-50 disabled:opacity-40">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </>
            ) : (
            <table data-testid="meta-ads-table" className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-zinc-50">
                  <th className="text-center px-3 py-3 w-10">
                    <Checkbox
                      data-testid="bulk-select-all"
                      checked={filteredSorted.length > 0 && allVisibleSelected(filteredSorted)}
                      onCheckedChange={() => toggleSelectAllVisible(filteredSorted)}
                      aria-label="Select all visible"
                    />
                  </th>
                  <th className="text-left px-3 py-3 data-label w-8"></th>
                  <th className="text-left px-2 py-3 data-label w-[88px]">Preview</th>
                  {COLUMNS.map((col) => (
                    <th key={col.key} data-testid={`th-${col.key}`}
                        onClick={() => handleSort(col.key)}
                        className={`px-5 py-3 data-label cursor-pointer select-none hover:text-zinc-900 ${col.align === "right" ? "text-right" : "text-left"}`}>
                      <span className="inline-flex items-center gap-1">{col.label} {sortIcon(col.key)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredSorted.length === 0 && !loading && (
                  <tr>
                    <td colSpan={COLUMNS.length + 3} className="text-center py-12 text-zinc-500 font-mono text-sm">
                      No ads match your filters.
                    </td>
                  </tr>
                )}
                {filteredSorted.map((ad) => {
                  const isOpen = !!expanded[ad.ad_name];
                  const isSelected = selectedAds.has(ad.ad_name);
                  return (
                    <React.Fragment key={ad.ad_name}>
                      <tr data-testid={`ad-row-${ad.ad_name}`} className={`border-b border-zinc-100 hover:bg-zinc-50 ${isSelected ? "bg-amber-50/40" : ""}`}>
                        <td className="px-3 py-2 text-center">
                          <Checkbox
                            data-testid={`bulk-select-${ad.ad_name}`}
                            checked={isSelected}
                            onCheckedChange={() => toggleAd(ad.ad_name)}
                            aria-label={`Select ${ad.ad_name}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <button data-testid={`expand-ad-${ad.ad_name}`}
                                  onClick={() => setExpanded((e) => ({ ...e, [ad.ad_name]: !e[ad.ad_name] }))}
                                  className="text-zinc-400 hover:text-zinc-900">
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-2 py-2 w-[88px]">
                          {ad.thumbnail_url ? (
                            <a href={ad.thumbnail_url} target="_blank" rel="noopener noreferrer"
                               data-testid={`thumb-${ad.ad_name}`}
                               className="block h-20 w-20 bg-zinc-100 border border-zinc-200 overflow-hidden hover:border-zinc-900">
                              <img src={ad.thumbnail_url} alt="" className="h-full w-full object-cover" loading="lazy" />
                            </a>
                          ) : (
                            <div className="h-20 w-20 bg-zinc-100 border border-zinc-200 flex items-center justify-center text-[9px] font-mono text-zinc-400">
                              no thumb
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 max-w-md">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-medium text-zinc-900 truncate">{ad.ad_name}</div>
                            {ad.overall_status === "running" && (
                              <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">Running</span>
                            )}
                            {ad.overall_status === "paused" && (
                              <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">Paused</span>
                            )}
                            {ad.overall_status === "archived" && (
                              <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-zinc-100 text-zinc-600 border border-zinc-200">Archived</span>
                            )}
                            <button
                              data-testid={`edit-ad-${ad.ad_name}`}
                              onClick={() => navigate(`/meta-ads/edit?account_id=${encodeURIComponent(ad.account_ids[0] || "")}&name=${encodeURIComponent(ad.ad_name)}`)}
                              title="Edit copy on all placements"
                              className="ml-1 text-zinc-400 hover:text-zinc-900"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              data-testid={`duplicate-ad-${ad.ad_name}`}
                              onClick={() => {
                                const aid = ad.account_ids[0] || "";
                                const adId = (ad.ad_ids || [])[0];
                                if (!adId) return toast.error("No ad id available");
                                navigate(`/meta-ads/create?source_ad=${adId}&account_id=${encodeURIComponent(aid)}`);
                              }}
                              title="Duplicate this ad (opens Create wizard pre-filled)"
                              className="text-zinc-400 hover:text-zinc-900"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="text-xs text-zinc-500 font-mono mt-1">{ad.breakdown.length} placements · {ad.account_ids.length} acct{ad.account_ids.length === 1 ? "" : "s"}</div>
                        </td>
                        {COLUMNS.slice(1).map((col) => (
                          <td key={col.key} className={`px-3 py-3 font-mono tabular-nums text-xs ${col.align === "right" ? "text-right" : ""}`}>
                            {col.variance ? (
                              ad[col.key] == null ? <span className="text-zinc-400">—</span> :
                                <span className={varianceClass(ad[col.key])} title={`TM ${ad.tm_registrations} vs Plat ${ad.registrations}`}>
                                  {fmtCell(col, ad[col.key])}
                                </span>
                            ) : col.colorize ? (
                              <span className={`px-1.5 py-0.5 ${cpaColorCls(ad[col.key])}`}>
                                {fmtCell(col, ad[col.key])}
                              </span>
                            ) : (
                              fmtCell(col, ad[col.key])
                            )}
                          </td>
                        ))}
                      </tr>
                      {isOpen && (
                        <tr className="bg-zinc-50">
                          <td colSpan={COLUMNS.length + 3} className="px-0 py-0">
                            <div className="border-l-2 border-zinc-900 ml-5">
                              <table className="w-full text-xs border-collapse">
                                <thead>
                                  <tr className="bg-zinc-100">
                                    <th className="text-left px-5 py-2 data-label">Campaign</th>
                                    <th className="text-left px-5 py-2 data-label">Ad set</th>
                                    <th className="text-right px-5 py-2 data-label">Spend</th>
                                    <th className="text-right px-5 py-2 data-label">Impr.</th>
                                    <th className="text-right px-5 py-2 data-label">Clicks</th>
                                    <th className="text-right px-5 py-2 data-label">CTR</th>
                                    <th className="text-right px-5 py-2 data-label">Plat. Reg.</th>
                                    <th className="text-right px-5 py-2 data-label">Plat. CPA</th>
                                    <th className="text-right px-5 py-2 data-label">TM Reg.</th>
                                    <th className="text-right px-5 py-2 data-label">TM CPA</th>
                                    <th className="text-right px-5 py-2 data-label">Var. %</th>
                                    <th className="text-right px-5 py-2 data-label">Camp ±20%</th>
                                    <th className="text-right px-5 py-2 data-label">Ad set ±20%</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {ad.breakdown.map((b, i) => {
                                    const tmReg = b.tm_registrations || 0;
                                    const platReg = b.registrations || 0;
                                    const variance = platReg > 0
                                      ? Math.round(((tmReg - platReg) / platReg) * 100)
                                      : (tmReg > 0 ? 100 : null);
                                    return (
                                    <tr key={i} className="border-b border-zinc-100">
                                      <td className="px-5 py-2 max-w-xs truncate">{b.campaign_name}</td>
                                      <td className="px-5 py-2 max-w-xs truncate">{b.adset_name}</td>
                                      <td className="px-5 py-2 text-right font-mono">{fmtMoney(b.spend)}</td>
                                      <td className="px-5 py-2 text-right font-mono">{fmtInt(b.impressions)}</td>
                                      <td className="px-5 py-2 text-right font-mono">{fmtInt(b.clicks)}</td>
                                      <td className="px-5 py-2 text-right font-mono">{fmtNum(b.ctr_clicks, 2)}%</td>
                                      <td className="px-5 py-2 text-right font-mono">{fmtInt(platReg)}</td>
                                      <td className="px-5 py-2 text-right font-mono">{platReg > 0 ? fmtMoney(b.cost_per_registration) : "—"}</td>
                                      <td className="px-5 py-2 text-right font-mono">{fmtNum(tmReg, 1)}</td>
                                      <td className="px-5 py-2 text-right font-mono">{tmReg > 0 ? fmtMoney(b.tm_cpa) : "—"}</td>
                                      <td className={`px-5 py-2 text-right font-mono ${variance === null ? "text-zinc-400" : Math.abs(variance) >= 25 ? "bg-red-50 text-red-700" : Math.abs(variance) >= 10 ? "bg-amber-50 text-amber-700" : "text-zinc-600"}`}>
                                        {variance === null ? "—" : `${variance > 0 ? "+" : ""}${variance}%`}
                                      </td>
                                      <td className="px-5 py-2 text-right whitespace-nowrap">
                                        <BudgetQuickPicker
                                          testid={`mads-camp-budget-${b.campaign_id}-${i}`}
                                          currentBudget={null}
                                          busy={quickBusy?.startsWith(`camp-${b.campaign_id}-`)}
                                          onApply={({ direction, pct }) => quickCampaign(b, direction, pct)}
                                        />
                                      </td>
                                      <td className="px-5 py-2 text-right whitespace-nowrap">
                                        <BudgetQuickPicker
                                          testid={`mads-ad-budget-${b.adset_id}-${i}`}
                                          currentBudget={null}
                                          busy={quickBusy?.startsWith(`ad-${b.adset_id}-`)}
                                          onApply={({ direction, pct }) => quickAdset(b, direction, pct)}
                                        />
                                      </td>
                                    </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
